// Helpers partagés de l'onglet Résultats (historique perso + suivi des partagés).

export const scoreColor = (pct) => pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : pct >= 50 ? '#f97316' : '#ef4444';

export const timeOfDay = (d) => d ? new Date(d.replace(' ', 'T')).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
export const dayKey = (d) => new Date(d.replace(' ', 'T')).toLocaleDateString('fr-CA'); // YYYY-MM-DD local
export const dayShort = (d) => new Date(d.replace(' ', 'T')).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
export const dayHeading = (d) => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const dd = new Date(d.replace(' ', 'T')); dd.setHours(0, 0, 0, 0);
    const diff = Math.round((t - dd) / 86400000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    return new Date(d.replace(' ', 'T')).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

export const formatTime = (s) => {
    if (!s) return '--';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const formatTimeLong = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
};

export const ORIGIN_LABELS = {
    melange: 'Quiz transversal',
    'audit-parcours': 'Bilan ciblé',
    'audit-diagnostic': 'Bilan guidé',
    'smart-mixed': 'Quiz intelligent',
    'smart-sm2-due': 'Révisions du jour',
    'smart-weaknesses': 'Faiblesses',
    'smart-revision': 'Révision',
    'smart-consolidation': 'Consolidation',
    'errors-never': 'Jamais réussies',
    'errors-sometimes': 'Instables',
    'errors-recent': 'Erreurs récentes',
};

// Suivi des résultats — avatars (dégradé déterministe par nom) + modes de tri de la cohorte.
const AVATAR_GRADIENTS = [
    'linear-gradient(135deg,#a78bfa,#60a5fa)',
    'linear-gradient(135deg,#34d399,#3b82f6)',
    'linear-gradient(135deg,#f472b6,#a78bfa)',
    'linear-gradient(135deg,#fbbf24,#f97316)',
    'linear-gradient(135deg,#22d3ee,#3b82f6)',
    'linear-gradient(135deg,#fb7185,#f43f5e)',
];
export const avatarGradient = (str) => AVATAR_GRADIENTS[[...(str || '?')].reduce((s, c) => s + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length];

export const SORT_MODES = {
    score: { label: 'Score', arrow: '↓', cmp: (a, b) => (b.success_rate ?? -1) - (a.success_rate ?? -1) },
    activity: { label: 'Activité', arrow: '↓', cmp: (a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0) },
    name: { label: 'Nom', arrow: '↑', cmp: (a, b) => (a.prenom || a.email || '').localeCompare(b.prenom || b.email || '') },
    quiz: { label: 'Quiz', arrow: '↓', cmp: (a, b) => (b.quiz_completed || 0) - (a.quiz_completed || 0) },
};
export const SORT_ORDER = ['score', 'activity', 'name', 'quiz'];

// Group entries: session_group entries become tree nodes, others stay flat
// Regroupe les sessions multi-sujets (mélange / SM2 / erreurs / audit) en un nœud
// dépliable ; les entrées simples restent à plat. Réutilisé pour l'historique perso
// ET pour l'aperçu « suivi des résultats » (read-only).
export const groupHistory = (entries) => {
    const groups = [];
    const groupMap = {};
    for (const entry of entries) {
        if (entry.session_group) {
            if (!groupMap[entry.session_group]) {
                groupMap[entry.session_group] = {
                    type: 'group', session_group: entry.session_group, origin: entry.origin,
                    mode: entry.mode, created_at: entry.created_at, children: [],
                    auto_closed: !!Number(entry.auto_closed),
                    // Réglages choisis au lancement (Mélange classique, migration 261), identiques sur tout le groupe.
                    time_limit_seconds: Number(entry.time_limit_seconds) || null,
                    target_pct: Number(entry.target_pct) || null,
                    planned_total: Number(entry.planned_total) || null,
                    totalScore: 0, totalQuestions: 0, totalTime: 0,
                };
                groups.push(groupMap[entry.session_group]);
            }
            const g = groupMap[entry.session_group];
            g.children.push(entry);
            g.totalScore += entry.score; g.totalQuestions += entry.total; g.totalTime += entry.time_in_seconds || 0;
        } else {
            groups.push({ type: 'single', entry });
        }
    }
    return groups;
};

// Séance laissée en cours puis enregistrée par le cron (close-stale-sessions.php) :
// seules les réponses données comptent, d'où un total inférieur au quiz lancé.
const AUTO_CLOSED_LABEL = 'arrêtée en cours, réponses données seulement';

// Durée avec les secondes : « 1min 5s sur 1min » et non « 1min sur 1min » pour une séance dépassée de peu.
const durationLabel = (t) => {
    const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
    return `${h ? `${h}h ` : ''}${m}min${sec ? ` ${sec}s` : ''}`;
};

// Réglages de la séance et verdicts : [{ key, text, ok }] (ok = true / false / null si non évalué).
// Séance arrêtée avant la fin : l'objectif n'est pas évalué (score sur les seules réponses données).
const sessionConditions = (g, pct) => {
    const out = [];
    if (g.target_pct) {
        const incomplete = g.planned_total && g.planned_total > g.totalQuestions;
        out.push(incomplete
            ? { key: 'target', text: `🎯 Objectif ${g.target_pct} % non évalué (séance arrêtée)`, ok: null }
            : { key: 'target', text: `🎯 Objectif ${g.target_pct} % ${pct >= g.target_pct ? 'atteint' : 'non atteint'}`, ok: pct >= g.target_pct });
    }
    if (g.time_limit_seconds) {
        const over = g.totalTime > g.time_limit_seconds;
        out.push({ key: 'time', text: `⏱ ${over ? 'Temps max dépassé' : 'Dans le temps'} (${durationLabel(g.totalTime)} sur ${durationLabel(g.time_limit_seconds)})`, ok: !over });
    }
    return out;
};

// Normalise un item (single | group) en modèle d'affichage uniforme.
export const normHistItem = (item) => {
    if (item.type === 'single') {
        const e = item.entry;
        const pct = e.total > 0 ? Math.round((e.score / e.total) * 100) : 0;
        const isGrouped = e.origin && e.origin !== 'subject' && !e.origin.startsWith('history:');
        const title = (isGrouped && (ORIGIN_LABELS[e.origin] || e.origin)) || e.sous_sujet_label;
        const meta = [e.categorie, Number(e.auto_closed) ? AUTO_CLOSED_LABEL : null].filter(Boolean).join(' · ');
        return { key: `e${e.id}`, kind: 'single', entry: e, group: null, pct, score: e.score, total: e.total, time: e.time_in_seconds, created_at: e.created_at, mode: e.mode, title, meta };
    }
    const g = item;
    const pct = g.totalQuestions > 0 ? Math.round((g.totalScore / g.totalQuestions) * 100) : 0;
    return { key: `g${g.session_group}`, kind: 'group', entry: null, group: g, pct, score: g.totalScore, total: g.totalQuestions, time: g.totalTime, created_at: g.created_at, mode: g.mode, title: ORIGIN_LABELS[g.origin] || g.origin || 'Session', conditions: sessionConditions(g, pct), meta: `${g.children.length} sujet${g.children.length > 1 ? 's' : ''}${g.auto_closed ? ` · ${AUTO_CLOSED_LABEL}` : ''}` };
};

/** Typographie française à l'affichage : espace insécable fine avant ; : ? ! et après « (évite une ligne qui commence par « ; »). */
export const nbspFr = (t) => String(t || '').replace(/\s+([;:!?»])/g, ' $1').replace(/«\s+/g, '« ');
